// MEGA-SPRINT Sprint 6 (V-3 Spelarvy) — "Mina matcher" page.
//
// Aggregates upcoming + past league matches across all the caller's teams,
// with per-match .ics export. Reuses the dual-identity team_members embed
// pattern from Sprint 4 (MySchedule.tsx) and the icsExport helper from
// Sprint 6 PR-base.
//
// Route: /leagues/mine (registered in src/App.tsx before /leagues/:id).

import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Download, Trophy } from "lucide-react";
import { fetchTeamsForUser } from "@/lib/teamMembershipQueries";
import { resolveTeamMember } from "@/lib/playerDisplay";
import { pickFirstTwoPrimaries } from "@/lib/teamHelpers";
import { buildMatchIcs, downloadIcsFile } from "@/lib/icsExport";

interface TeamMember {
  user_id: string | null;
  customer_id: string | null;
  role: "primary" | "reserve";
  joined_at: string;
  profile: { full_name: string | null } | null;
  customer: { full_name: string | null; email: string | null } | null;
}

interface TeamLite {
  id: string;
  team_name: string | null;
  members?: TeamMember[] | null;
}

interface MatchRow {
  id: string;
  league_id: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  status: string;
  home_team_id: string;
  away_team_id: string;
  home_team: TeamLite | null;
  away_team: TeamLite | null;
  court: { name: string } | null;
  league: { id: string; name: string } | null;
}

function teamLabel(team: TeamLite | null): string {
  if (!team) return "—";
  if (team.team_name) return team.team_name;
  const [p1, p2] = pickFirstTwoPrimaries((team.members ?? null) as TeamMember[] | null);
  const firstName = (m: TeamMember | null) => {
    if (!m) return "?";
    const r = resolveTeamMember(m);
    if (r.kind === "none") return "?";
    return r.displayName.split(" ")[0] || "?";
  };
  return `${firstName(p1)} / ${firstName(p2)}`;
}

export default function LeaguesMine() {
  const { user } = useAuth();
  const { t } = useTranslation("common");

  // Step 1 — find all teams the user belongs to.
  const { data: myTeams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ["leagues-mine-teams", user?.id],
    queryFn: async () => {
      if (!user?.id) return [] as Array<{ id: string }>;
      return await fetchTeamsForUser<{ id: string }>(supabase, user.id, {
        columns: "id",
      });
    },
    enabled: !!user?.id,
  });

  const teamIds = myTeams.map((t) => t.id);

  // Step 2 — fetch all matches for those teams (upcoming + past).
  const { data: matches = [], isLoading: matchesLoading } = useQuery({
    queryKey: ["leagues-mine-matches", teamIds.join(",")],
    queryFn: async () => {
      if (teamIds.length === 0) return [] as MatchRow[];
      const { data, error } = await (supabase as any)
        .from("league_matches")
        .select(`
          id, league_id, scheduled_date, scheduled_time, status,
          home_team_id, away_team_id,
          home_team:teams!league_matches_home_team_id_fkey(
            id, team_name,
            members:team_members(user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name),
              customer:customers!team_members_customer_id_fkey(full_name, email))
          ),
          away_team:teams!league_matches_away_team_id_fkey(
            id, team_name,
            members:team_members(user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name),
              customer:customers!team_members_customer_id_fkey(full_name, email))
          ),
          court:courts(name),
          league:leagues!league_matches_league_id_fkey(id, name)
        `)
        .or(`home_team_id.in.(${teamIds.join(",")}),away_team_id.in.(${teamIds.join(",")})`)
        .order("scheduled_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as MatchRow[];
    },
    enabled: teamIds.length > 0,
  });

  const myTeamIdSet = new Set(teamIds);
  const now = Date.now();

  const splitMatches = (rows: MatchRow[]) => {
    const upcoming: MatchRow[] = [];
    const past: MatchRow[] = [];
    for (const m of rows) {
      if (!m.scheduled_date || !m.scheduled_time) {
        upcoming.push(m);
        continue;
      }
      const t = new Date(`${m.scheduled_date}T${m.scheduled_time}`).getTime();
      if (Number.isFinite(t) && t < now) past.push(m);
      else upcoming.push(m);
    }
    upcoming.sort((a, b) => (a.scheduled_date ?? "").localeCompare(b.scheduled_date ?? ""));
    return { upcoming, past };
  };
  const { upcoming, past } = splitMatches(matches);

  const handleDownloadIcs = (m: MatchRow) => {
    if (!m.scheduled_date || !m.scheduled_time) return;
    const myTeam = myTeamIdSet.has(m.home_team_id) ? m.home_team : m.away_team;
    const opponent = myTeamIdSet.has(m.home_team_id) ? m.away_team : m.home_team;
    const opponentLabel = teamLabel(opponent);
    const ics = buildMatchIcs({
      matchId: m.id,
      scheduledDate: m.scheduled_date,
      scheduledTime: m.scheduled_time,
      leagueName: m.league?.name ?? t("leaguesMine.leagueFallback"),
      homeTeamName: teamLabel(m.home_team),
      awayTeamName: teamLabel(m.away_team),
      courtName: m.court?.name ?? null,
      opponentLabel,
      matchDeepLink: `${window.location.origin}/leagues/${m.league_id}`,
    });
    const slug = (m.league?.name ?? "match").replace(/\s+/g, "-").toLowerCase();
    downloadIcsFile(`${slug}-${m.scheduled_date}.ics`, ics);
  };

  if (!user) {
    return (
      <div className="container py-8">
        <p className="text-muted-foreground">{t("leaguesMine.loginToSeeMatches")}</p>
      </div>
    );
  }

  if (teamsLoading || matchesLoading) {
    return (
      <div className="container py-8 space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const renderMatchCard = (m: MatchRow) => {
    const opponent = myTeamIdSet.has(m.home_team_id) ? m.away_team : m.home_team;
    return (
      <Card key={m.id} className="overflow-hidden">
        <CardContent className="flex items-center justify-between gap-4 p-4">
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">
              {t("leaguesMine.vsPrefix", { opponent: teamLabel(opponent) })}
            </div>
            <div className="text-sm text-muted-foreground truncate">
              {m.league?.name ?? "—"} · {m.scheduled_date ?? "—"}
              {m.scheduled_time ? ` ${m.scheduled_time.slice(0, 5)}` : ""}
              {m.court?.name ? ` · ${m.court.name}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownloadIcs(m)}
              disabled={!m.scheduled_date || !m.scheduled_time}
              aria-label={t("leaguesMine.downloadIcsAria")}
            >
              <Download className="h-4 w-4 mr-2" />
              .ics
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to={`/leagues/${m.league_id}`}>{t("leaguesMine.details")}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Calendar className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">{t("myLeagueMatches")}</h1>
      </div>

      {matches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Trophy className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>{t("leaguesMine.noMatchesYet")}</p>
            <Button asChild variant="link" className="mt-2">
              <Link to="/leagues">{t("leaguesMine.browseLeagues")}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">
              {t("leaguesMine.tabs.upcoming", { count: upcoming.length })}
            </TabsTrigger>
            <TabsTrigger value="past">
              {t("leaguesMine.tabs.past", { count: past.length })}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="upcoming" className="space-y-3 pt-4">
            {upcoming.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("leaguesMine.noUpcomingScheduled")}
              </p>
            ) : (
              upcoming.map(renderMatchCard)
            )}
          </TabsContent>
          <TabsContent value="past" className="space-y-3 pt-4">
            {past.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("leaguesMine.noHistoryYet")}
              </p>
            ) : (
              past.map(renderMatchCard)
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
